<template>
  <b-col cols="12">
    <b-row>
      <b-col cols="12">
        <hr class="my-2">
        <h5 class="mb-0">
          صلاحيات الموقع — المحافظات والمراكز
        </h5>
        <small class="text-muted d-block mb-3">
          يحدد المشاريع التي يمكن للمستخدم رؤيتها حسب المحافظة أو المركز
        </small>
      </b-col>

      <b-col
        cols="12"
        md="6"
        class="mb-2"
      >
      <b-form-group label="المحافظات">
        <v-select
          v-model="localGovernorateIds"
          :options="governoratesDropdown"
          :reduce="val => val.id"
          :get-option-label="governorateLabel"
          multiple
          :disabled="disabled || loading"
          :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
          placeholder="اختر المحافظات"
        >
          <template #option="option">
            <span>{{ governorateLabel(option) }}</span>
          </template>
          <template #selected-option="option">
            <span>{{ governorateLabel(option) }}</span>
          </template>
          <template #no-options>
            لا توجد محافظات
          </template>
        </v-select>
        <small class="text-muted">
          اختيار محافظة يمنح رؤية جميع مشاريعها
        </small>
        <small
          v-for="(err, idx) in fieldErrors('governorate_ids')"
          :key="'gov-err-' + idx"
          class="text-danger d-block"
        >{{ err }}</small>
      </b-form-group>
    </b-col>

    <b-col
      cols="12"
      md="6"
      class="mb-2"
    >
      <b-form-group label="المراكز">
        <v-select
          v-model="localCenterIds"
          :options="centersDropdown"
          :reduce="val => val.id"
          :get-option-label="centerLabel"
          multiple
          :disabled="disabled || loading"
          :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
          placeholder="اختر المراكز"
        >
          <template #option="option">
            <span>{{ centerLabel(option) }}</span>
          </template>
          <template #selected-option="option">
            <span>{{ centerLabel(option) }}</span>
          </template>
          <template #no-options>
            لا توجد مراكز
          </template>
        </v-select>
        <small class="text-muted">
          اختيار مركز يمنح رؤية مشاريع هذا المركز فقط — مستقل عن المحافظة
        </small>
        <small
          v-for="(err, idx) in fieldErrors('center_ids')"
          :key="'center-err-' + idx"
          class="text-danger d-block"
        >{{ err }}</small>
      </b-form-group>
      </b-col>
    </b-row>
  </b-col>
</template>

<script>
import { BCol, BRow, BFormGroup } from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'UserLocationAccessSection',
  components: {
    BCol,
    BRow,
    BFormGroup,
    vSelect,
  },
  props: {
    userId: {
      type: [Number, String],
      default: null,
    },
    governorateIds: {
      type: Array,
      default: () => [],
    },
    centerIds: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      initialGovernorateIds: [],
      initialCenterIds: [],
    }
  },
  computed: {
    ...mapGetters({
      governoratesDropdown: 'governorates/dropdown',
      centersDropdown: 'centers/dropdown',
    }),
    governoratesMap() {
      return this.governoratesDropdown.reduce((acc, gov) => {
        acc[gov.id] = gov
        return acc
      }, {})
    },
    localGovernorateIds: {
      get() {
        return this.governorateIds
      },
      set(val) {
        this.$emit('update:governorateIds', val || [])
      },
    },
    localCenterIds: {
      get() {
        return this.centerIds
      },
      set(val) {
        this.$emit('update:centerIds', val || [])
      },
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.loadUserLocationAccess(id)
        } else {
          this.resetInitialAccess()
        }
      },
    },
  },
  mounted() {
    this.loadDropdowns()
  },
  methods: {
    loadDropdowns() {
      if (!this.governoratesDropdown.length) {
        this.$store.dispatch('governorates/dropdown')
      }
      if (!this.centersDropdown.length) {
        this.$store.dispatch('centers/dropdown')
      }
    },
    governorateLabel(option) {
      if (!option) return ''
      return option.name_ar || option.name_en || ''
    },
    centerLabel(option) {
      if (!option) return ''
      const centerName = option.name_ar || option.name_en || ''
      const gov = this.governoratesMap[option.governorate_id]
      const govName = gov ? (gov.name_ar || gov.name_en) : ''
      return govName ? `${centerName} (${govName})` : centerName
    },
    fieldErrors(prefix) {
      if (!this.validationErrors) return []
      return Object.entries(this.validationErrors)
        .filter(([key]) => key.startsWith(prefix))
        .flatMap(([, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]))
    },
    resetInitialAccess() {
      this.initialGovernorateIds = []
      this.initialCenterIds = []
    },
    loadUserLocationAccess(userId) {
      this.loading = true
      this.$store.dispatch('users/getLocationAccess', userId)
        .then(response => {
          const data = response.data || {}
          const governorateIds = (data.governorates || []).map(g => g.id)
          const centerIds = (data.centers || []).map(c => c.id)
          this.initialGovernorateIds = [...governorateIds]
          this.initialCenterIds = [...centerIds]
          this.$emit('update:governorateIds', governorateIds)
          this.$emit('update:centerIds', centerIds)
        })
        .catch(error => {
          if (error.response && error.response.status === 403) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'ليس لديك صلاحية',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    hadPreviousAccess() {
      return this.initialGovernorateIds.length > 0 || this.initialCenterIds.length > 0
    },
    isClearingAllAccess() {
      return this.governorateIds.length === 0 && this.centerIds.length === 0
    },
    confirmClearAccessIfNeeded() {
      if (!this.hadPreviousAccess() || !this.isClearingAllAccess()) {
        return Promise.resolve(true)
      }
      return this.$swal({
        icon: 'warning',
        title: 'مسح صلاحيات الموقع؟',
        text: 'سيتم إزالة جميع المحافظات والمراكز المعينة للمستخدم. هل أنت متأكد؟',
        showCancelButton: true,
        confirmButtonText: 'نعم، مسح',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#d33',
      }).then(result => result.isConfirmed)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
